/**
 * The code lists and the screener criteria, through the worker.
 *
 * Same boundary rules as the chart client: only `VITE_QUANTIST_API_BASE` is
 * ever called, the API key lives in the worker, and nothing here names a
 * hosted API hostname.
 *
 * Both degrade to the bundled fixtures rather than failing the page. A list
 * only decides what the dropdown *offers*; the chart it opens is live either
 * way, so an offline list is a smaller lie than a blank workspace — as long
 * as the UI says which one it is, which `origin` is for.
 */
import { apiBaseUrl, ChartRequestError } from "./client.ts";
import { displayNameFor, resolveCode, type InstrumentOption } from "../domain/instruments.ts";
import { INSTRUMENT_FIXTURE, SCREENER_HINTS, SCREENERS, type ScreenerEntry } from "../fixtures/instruments.ts";

export type CatalogOrigin = "api" | "fixture";

export interface CatalogSource {
	baseUrl?: string | null;
	fetchImpl?: typeof fetch;
	signal?: AbortSignal;
}

export interface CatalogResult<T> {
	items: readonly T[];
	origin: CatalogOrigin;
}

/** One row of `/param/list/{category}` — the existing API list route. */
interface ListRow {
	index: number;
	code: string;
}

/** Mirrors `ScreenerCatalog` in quantist_api's web_contract.py. */
interface ScreenerCatalogPayload {
	screeners: Array<{
		slug: string;
		label: string;
		group: string;
		methods: string[];
		results_endpoint: string;
		web_results_available: boolean;
	}>;
}

/**
 * The code lists that populate the dropdown.
 *
 * `/param/list/broker` exists and the worker caches it too, but broker codes
 * are counterparties, not something you can open a chart for, so the dropdown
 * asks only for the two it can navigate to.
 */
export const LIST_PATHS = { stock: "/param/list/stock", index: "/param/list/index" } as const;
export const CATALOG_CATEGORIES = ["stock", "index"] as const;

async function readJson<T>(path: string, source: CatalogSource): Promise<T> {
	const baseUrl = source.baseUrl === undefined ? apiBaseUrl() : source.baseUrl;
	if (!baseUrl) throw new ChartRequestError("No API base URL is configured.");

	const request = source.fetchImpl ?? fetch;
	// The worker base ends in /web-api/v1; list paths live at the worker root,
	// so they are resolved against the origin rather than appended to the base.
	const root = baseUrl.replace(/\/+$/, "");
	const url = path.startsWith("/param/") ? new URL(path, root).toString() : `${root}${path}`;
	const response = await request(url, {
		headers: { accept: "application/json" },
		...(source.signal ? { signal: source.signal } : {}),
	});
		if (!response.ok) throw new ChartRequestError(`Request "${path}" returned ${response.status}.`, response.status);
	return (await response.json()) as T;
}

function toOptions(rows: unknown, category: "stock" | "index"): InstrumentOption[] {
	if (!Array.isArray(rows)) throw new ChartRequestError(`The ${category} list is not a list.`);
	return (rows as ListRow[])
		.filter((entry) => typeof entry?.code === "string" && entry.code !== "" && Number.isFinite(Number(entry.index)))
		.map((entry) => {
			const code = resolveCode(entry.code);
			return {
				code,
				name: displayNameFor(code),
				// The list route has no sector column; saying "Stock" is true,
				// inventing "Financials" would not be.
				sector: category === "index" ? "Index" : "Stock",
				// Carried through exactly: the row's own index from the list
				// route, never a position in this array and never renumbered.
				index: Number(entry.index),
			};
		});
}

/**
 * The live stock and index lists, merged, or the bundled one.
 *
 * Both are fetched because the dropdown is one list: COMPOSITE has to be
 * findable next to BBRI. Codes go through `resolveCode`, so IHSG/COMPOSITE
 * collapses here exactly as it does in the chart path.
 */
export async function fetchInstrumentCatalog(source: CatalogSource = {}): Promise<CatalogResult<InstrumentOption>> {
	try {
		const lists = await Promise.all(CATALOG_CATEGORIES.map((category) => readJson<unknown>(LIST_PATHS[category], source)));
		const merged = new Map<string, InstrumentOption>();
		// Index first, so COMPOSITE heads the list the way the fixture had it.
		for (const option of [...toOptions(lists[1], "index"), ...toOptions(lists[0], "stock")]) {
			if (!merged.has(option.code)) merged.set(option.code, option);
		}
		if (merged.size === 0) throw new ChartRequestError("The code lists are empty.");
		return { items: [...merged.values()], origin: "api" };
	} catch {
		return { items: INSTRUMENT_FIXTURE, origin: "fixture" };
	}
}

/**
 * Live screener metadata, or the bundled list.
 *
 * `label` and `group` come from the API; the one-line `hint` is product copy
 * that lives here, keyed by slug. A slug the copy does not know about still
 * renders — it just shows its endpoint status instead of a description.
 *
 * `web_results_available` is carried through untouched as `resultsAvailable`,
 * so the UI shows what can actually be fetched rather than assuming.
 */
export async function fetchScreenerCatalog(source: CatalogSource = {}): Promise<CatalogResult<ScreenerEntry>> {
	try {
		const payload = await readJson<ScreenerCatalogPayload>("/screeners", source);
		if (!Array.isArray(payload?.screeners) || payload.screeners.length === 0) {
			throw new ChartRequestError("Screener catalogue is empty.");
		}
		return {
			items: payload.screeners.map((entry) => ({
				slug: entry.slug,
				label: entry.label || entry.slug,
				group: entry.group || "Other",
				hint: SCREENER_HINTS[entry.slug] ?? "",
				resultsAvailable: entry.web_results_available === true,
			})),
			origin: "api",
		};
	} catch {
		return { items: SCREENERS, origin: "fixture" };
	}
}
